// testSlice.ts
import { createSlice, createAsyncThunk, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { AxiosError } from "axios";
import apiService from "../../services/api.services";

// types
export interface TestQuestion {
  id: number;
  questionText: string;
  options: {
    id: number;
    text: string;
  }[];
}

export interface TestSession {
  sessionId: number;
  timeLimit: number;
  questions: TestQuestion[];
  title?: string;
  attemptsUsed?: number;
  attemptsAllowed?: number;
  attemptsRemaining?: number;
}

export interface TestSubmission {
  questionId: number;
  selectedOptionId: number;
}

export interface TestState {
  session: TestSession | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  selectedAnswers: Record<number, number>;
  timeRemaining: number;
  currentQuestionIndex: number;
  isSubmitting: boolean;
  lastFetched: Record<string, number>;
  offlineQueue: OfflineQueueItem[];
  activeRequests: Record<string, boolean>;
  flaggedQuestions: number[]; // New state for flagged questions
  testHistory: TestHistoryItem[];
  testHistoryStatus: "idle" | "loading" | "succeeded" | "failed";
  testHistoryError: string | null;
}

interface OfflineQueueItem {
  type: "SUBMIT";
  payload: {
    sessionId: number;
    submissions: TestSubmission[];
  };
  timestamp: number;
}

const OFFLINE_STORAGE_KEY = "test_session_data";

export interface TestHistoryItem {
  id: number;
  student_id: number;
  template_id: number;
  started_at: string;
  completed_at: string;
  time_limit: number;
  time_taken: number;
  status: string;
  score: number;
  created_at: string;
  test_title: string;
  course_id: number;
  course_title: string;
  total_questions: number;
  correct_answers: number;
  performance_percentage: number;
}

const initialState: TestState = {
  session: null,
  status: "idle",
  error: null,
  selectedAnswers: {},
  timeRemaining: 0,
  currentQuestionIndex: 0,
  isSubmitting: false,
  lastFetched: {},
  offlineQueue: [],
  activeRequests: {},
  flaggedQuestions: [], // Initialize empty array for flagged questions
  testHistory: [],
  testHistoryStatus: "idle",
  testHistoryError: null,
};

// Helper functions for offline storage
const saveToOfflineStorage = (data: TestState) => {
  try {
    localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Failed to save to offline storage:", error);
  }
};



// Async thunks
export const startTestSession = createAsyncThunk(
  "test/startSession",
  async (templateId: number, { rejectWithValue }) => {
    try {
      const response = await apiService.post<TestSession>(
        `/test/templates/${templateId}/start`,
        {}
      );
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to start test session";
      return rejectWithValue({ message: errorMessage });
    }
  }
);

export const submitTestSession = createAsyncThunk(
  "test/submitSession",
  async (
    {
      sessionId,
      submissions,
    }: { sessionId: number; submissions: TestSubmission[] },
    { rejectWithValue, getState }
  ) => {
    // Handle offline submission
    if (!navigator.onLine) {
      toast.info(
        "You are offline. Test will be submitted when connection is restored."
      );
      return { offline: true, sessionId, submissions };
    }

    try {
      const response = await apiService.post<TestSession>(
        `/test/sessions/${sessionId}/submit`,
        { submissions }
      );
      toast.success("Test submitted successfully!");
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to submit test";
      toast.error(errorMessage);
      return rejectWithValue({ message: errorMessage });
    }
  }
);

export const getTestHistory = createAsyncThunk(
  "test/getTestHistory",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get<TestHistoryItem[]>("/test/history");
      return response;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to fetch test history";
      toast.error(errorMessage);
      return rejectWithValue({ message: errorMessage });
    }
  }
);

// Process offline queue
export const processOfflineQueue = createAsyncThunk(
  "test/processOfflineQueue",
  async (_, { getState, dispatch }) => {
    const state = getState() as { test: TestState };
    const queue = [...state.test.offlineQueue];

    for (const item of queue) {
      try {
        if (item.type === "SUBMIT") {
          await dispatch(submitTestSession(item.payload)).unwrap();
        }
      } catch (error) {
        console.error("Failed to process offline item:", error);
      }
    }
  }
);

const testSlice = createSlice({
  name: "test",
  initialState,
  reducers: {
    setCurrentQuestionIndex: (state, action: PayloadAction<number>) => {
      state.currentQuestionIndex = action.payload;
    },
    setAnswer: (
      state,
      action: PayloadAction<{ questionId: number; optionId: number }>
    ) => {
      state.selectedAnswers[action.payload.questionId] =
        action.payload.optionId;
      saveToOfflineStorage(state);
    },

    updateTimeRemaining: (state, action: PayloadAction<number>) => {
      state.timeRemaining = action.payload;
    },
    addToOfflineQueue: (state, action: PayloadAction<OfflineQueueItem>) => {
      state.offlineQueue.push(action.payload);
      saveToOfflineStorage(state);
    },
    removeFromOfflineQueue: (state, action: PayloadAction<number>) => {
      state.offlineQueue = state.offlineQueue.filter(
        (item) => item.timestamp !== action.payload
      );
      saveToOfflineStorage(state);
    },
    // New reducer for flagging questions
    flagQuestion: (state, action: PayloadAction<number>) => {
      const questionId = action.payload;

      // If question is already flagged, remove it from flagged questions
      if (state.flaggedQuestions.includes(questionId)) {
        state.flaggedQuestions = state.flaggedQuestions.filter(
          (id) => id !== questionId
        );
      } else {
        // Otherwise add it to flagged questions
        state.flaggedQuestions.push(questionId);
      }

      saveToOfflineStorage(state);
    },
    // Clear all flagged questions
    clearFlaggedQuestions: (state) => {
      state.flaggedQuestions = [];
      saveToOfflineStorage(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startTestSession.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(startTestSession.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.session = action.payload;
        state.timeRemaining = action.payload.timeLimit * 60;
        // Reset flagged questions when starting a new test
        state.flaggedQuestions = [];
        saveToOfflineStorage(state);
      })
      .addCase(startTestSession.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(submitTestSession.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(submitTestSession.fulfilled, (state, action) => {
        if (!action.payload) {
          state.isSubmitting = false;
          state.session = null;
          state.selectedAnswers = {};
          state.timeRemaining = 0;
          state.flaggedQuestions = []; // Clear flagged questions on successful submission
          localStorage.removeItem(OFFLINE_STORAGE_KEY);
        }
      })
      .addCase(submitTestSession.rejected, (state) => {
        state.isSubmitting = false;
      })
      .addCase(getTestHistory.pending, (state) => {
        state.testHistoryStatus = "loading";
        state.testHistoryError = null;
      })
      .addCase(getTestHistory.fulfilled, (state, action) => {
        state.testHistoryStatus = "succeeded";
        state.testHistory = action.payload;
      })
      .addCase(getTestHistory.rejected, (state, action) => {
        state.testHistoryStatus = "failed";
        state.testHistoryError = action.payload as string;
      })
      .addCase(processOfflineQueue.fulfilled, (state) => {
        state.offlineQueue = [];
        saveToOfflineStorage(state);
      });
  },
});

export const {
  setCurrentQuestionIndex,
  setAnswer,
  updateTimeRemaining,
  addToOfflineQueue,
  removeFromOfflineQueue,
  flagQuestion, // Export the new action
  clearFlaggedQuestions, // Export the clear action
} = testSlice.actions;



// Selectors
export const selectTestSession = (state: { test: TestState }) =>
  state.test.session;
export const selectCurrentQuestion = (state: { test: TestState }) =>
  state.test.session?.questions[state.test.currentQuestionIndex];
export const selectSelectedAnswers = (state: { test: TestState }) =>
  state.test.selectedAnswers;
export const selectTimeRemaining = (state: { test: TestState }) =>
  state.test.timeRemaining;
export const selectTestStatus = (state: { test: TestState }) =>
  state.test.status;
export const selectCurrentQuestionIndex = (state: { test: TestState }) =>
  state.test.currentQuestionIndex;
// New selector for flagged questions
export const selectFlaggedQuestions = (state: { test: TestState }) =>
  state.test.flaggedQuestions;

export const selectTestHistory = (state: { test: TestState }) =>
  state.test.testHistory;

export const selectTestHistoryStatus = (state: { test: TestState }) =>
  state.test.testHistoryStatus;

export const selectTestHistoryError = (state: { test: TestState }) =>
  state.test.testHistoryError;
// Network listeners setup
export const setupTestNetworkListeners = (store: any) => {
  window.addEventListener("online", () => {
    store.dispatch(processOfflineQueue());
    toast.success("Back online. Syncing test data...");
  });

  window.addEventListener("offline", () => {
    toast.info("You are offline. Your answers will be saved locally.");
  });
};
export const selectTestHistoryById = createSelector(
  [
    (state: { test: TestState }) => state.test.testHistory,
    (_: any, id: number) => id,
  ],
  (testHistory, id): TestHistoryItem | undefined => testHistory.find(item => item.id === id)
);

export default testSlice.reducer;
